import { render, h } from '../utils/jsx-to-html';
import { UpdateBanner } from '../Component/UpdateBanner';
import { UpdateMessages } from '../types/update.types';
import { reloadPageWithoutCache } from '../utils/reloadPage';

/**
 * Service class responsible for managing the update UI banner in the application.
 * Handles the creation and display of an update notification banner.
 */
export class UpdateUI {
  /** The DOM element that contains the update banner */
  private readonly container: HTMLElement;
  /** Tracks whether the banner has been rendered at least once */
  private isRendered = false;

  /**
   * Creates a new UpdateUI instance.
   * @param containerId - The ID for the container element (defaults to 'update-banner-container')
   */
  constructor(containerId = 'update-banner-container') {
    this.container = this.createContainer(containerId);
  }

  /**
   * Renders the update banner with the specified messages and countdown timer.
   * @param messages - The messages to display in the banner
   * @param timeLeftInSeconds - The time remaining before the update action
   */
  public displayBanner(
    messages: UpdateMessages,
    timeLeftInSeconds: number,
  ): void {
    render(
      <UpdateBanner 
        isRendered={this.isRendered} 
        messages={messages} 
        timeLeftInSeconds={timeLeftInSeconds} 
        onReload={reloadPageWithoutCache} 
      />,
      this.container
    );
    this.isRendered = true;
  }

  /**
   * Creates or retrieves the container element for the update banner.
   * @param id - The ID of the container element
   * @returns The container HTML element
   * @private
   */
  private createContainer(id: string): HTMLElement {
    const existing = document.getElementById(id);
    if (existing) {
      return existing;
    }

    const container = document.createElement('div');
    container.id = id;
    document.body.appendChild(container);
    return container;
  }
} 